import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LongPage, TocEntry } from "./SideNav";
import { Bullet, Bullets, Mail, PageHeader, Pill, Section } from "./ui";
import { CONTACT_EMAIL } from "./constants";

interface Release {
  id: string;
  version: string;
  tone: "now" | "next" | "later";
  items: { key: string; partial?: boolean }[];
}

const RELEASES: Release[] = [
  {
    id: "v1_0",
    version: "1.0.0",
    tone: "now",
    items: [
      { key: "sharingMode" },
      { key: "directShare" },
      { key: "removeFiles" },
      { key: "multiFileRequest", partial: true },
      { key: "excelDownload", partial: true },
      { key: "securityAudit" },
    ],
  },
  {
    id: "v1_1",
    version: "1.1.0",
    tone: "next",
    items: [
      { key: "redcap" },
      { key: "redcapLinks" },
      { key: "auditHistory" },
      { key: "reidentification" },
      { key: "editAfterUpload" },
      { key: "promoteToPi" },
      { key: "shareEndDate" },
      { key: "passThrough" },
      { key: "sasExport" },
    ],
  },
  {
    id: "v1_2",
    version: "1.2.0",
    tone: "next",
    items: [
      { key: "batchUpload" },
      { key: "starterPackage" },
      { key: "nextcloud" },
    ],
  },
  {
    id: "later",
    version: "",
    tone: "later",
    items: [
      { key: "jupyterhub" },
      { key: "remoteWorkspace" },
      { key: "crossProject" },
      { key: "lockProject" },
    ],
  },
];

const CARD_TONES = {
  now: "border-amber-200 bg-amber-50/50",
  next: "border-sky-200 bg-sky-50/50",
  later: "border-sand-200 bg-sand-50",
} as const;

export const RoadmapPage: React.FC = () => {
  const { t } = useTranslation("roadmapPage");

  const TOC: TocEntry[] = [
    { id: "today", label: t("toc.today") },
    {
      id: "planned",
      label: t("toc.planned"),
      children: RELEASES.map((r) => ({
        id: `release-${r.id}`,
        label: r.version ? `v${r.version}` : t(`sections.planned.releases.${r.id}.title`),
      })),
    },
    { id: "influence", label: t("toc.influence") },
  ];

  return (
    <LongPage
      toc={TOC}
      intro={
        <>
          <PageHeader
            kicker={t("header.kicker")}
            title={t("header.title")}
            lead={
              <p className="font-display text-2xl italic leading-snug text-ink-600">
                {(t("header.epigraph", { returnObjects: true }) as string[]).map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </p>
            }
          />
        </>
      }
    >
      <div className="divide-y divide-sand-200 [&>section:not(:first-child)]:pt-14 [&>section]:pb-14 [&>section:last-child]:pb-0">
        <Section id="today" number="01" title={t("sections.today.title")}>
          <p>{t("sections.today.body")}</p>
          <div className="mt-6 rounded-xl border border-accent/25 bg-accent-pale/40 px-6 py-5">
            <div className="flex flex-wrap items-center gap-3">
              <Pill tone="done">{t("sections.today.badge")}</Pill>
              <p className="font-semibold text-ink">{t("sections.today.intro")}</p>
            </div>
          <Bullets tight>
            <Bullet>
              <strong>{t("sections.today.items.pseudonymization.title")}</strong> —{" "}
              {t("sections.today.items.pseudonymization.body")}
            </Bullet>
            <Bullet>
              <strong>{t("sections.today.items.accounts.title")}</strong> —{" "}
              {t("sections.today.items.accounts.body")}
            </Bullet>
            <Bullet>
              <strong>{t("sections.today.items.catalogue.title")}</strong> —{" "}
              {t("sections.today.items.catalogue.body")}
            </Bullet>
            <Bullet>
              <strong>{t("sections.today.items.requests.title")}</strong> —{" "}
              {t("sections.today.items.requests.body")}
            </Bullet>
            <Bullet>
              <strong>{t("sections.today.items.screening.title")}</strong> —{" "}
              {t("sections.today.items.screening.body")}
            </Bullet>
            <Bullet>
              <strong>{t("sections.today.items.expiry.title")}</strong> —{" "}
              {t("sections.today.items.expiry.body")}
            </Bullet>
            <Bullet>
              <strong>{t("sections.today.items.archiving.title")}</strong> —{" "}
              {t("sections.today.items.archiving.body")}
            </Bullet>
            <Bullet>
              <strong>{t("sections.today.items.notifications.title")}</strong> —{" "}
              {t("sections.today.items.notifications.body")}
            </Bullet>
          </Bullets>
          </div>
        </Section>

        <Section id="planned" number="02" title={t("sections.planned.title")}>
          <p>{t("sections.planned.intro")}</p>
          <div className="mt-6 space-y-6">
            {RELEASES.map((release) => (
              <div
                key={release.id}
                id={`release-${release.id}`}
                className={`scroll-mt-28 rounded-xl border px-6 py-5 ${CARD_TONES[release.tone]}`}
              >
                <div className="flex flex-wrap items-center gap-3">
                  {release.version && (
                    <span className="font-display text-2xl text-ink">v{release.version}</span>
                  )}
                  <Pill tone={release.tone}>{t(`sections.planned.releases.${release.id}.badge`)}</Pill>
                </div>
                <p className="mt-1 font-semibold text-ink">
                  {t(`sections.planned.releases.${release.id}.title`)}
                </p>
                <p className="site-prose mt-1 text-[0.9375rem]">
                  {t(`sections.planned.releases.${release.id}.summary`)}
                </p>
                <Bullets tight>
                  {release.items.map((item) => (
                    <Bullet key={item.key}>
                      <strong>{t(`sections.planned.items.${item.key}.title`)}</strong>
                      {item.partial && (
                        <em className="ml-2 text-sm text-ink-600">({t("sections.planned.partial")})</em>
                      )}{" "}
                      — {t(`sections.planned.items.${item.key}.body`)}
                    </Bullet>
                  ))}
                </Bullets>
              </div>
            ))}
          </div>
        </Section>

        <Section id="influence" number="03" title={t("sections.influence.title")}>
          <p>
            {t("sections.influence.intro.before")} <Mail address={CONTACT_EMAIL} />{" "}
            {t("sections.influence.intro.after")}
          </p>
          <div className="my-7 rounded-xl border border-accent/25 bg-accent-pale/50 px-6 py-5">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-600">
              {t("sections.influence.writeToUsBox.kicker")}
            </p>
            <p className="mt-2 font-display text-2xl text-ink">
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline decoration-accent/50 underline-offset-4 hover:decoration-accent">
                {CONTACT_EMAIL}
              </a>
            </p>
            <div className="site-prose mt-4 text-[0.9375rem]">
              <p className="mb-2">{t("sections.influence.writeToUsBox.welcomeIntro")}</p>
            </div>
            <Bullets tight>
              <Bullet>
                <strong>{t("sections.influence.writeToUsBox.items.suggestions.title")}</strong> —{" "}
                {t("sections.influence.writeToUsBox.items.suggestions.body")}
              </Bullet>
              <Bullet>
                <strong>{t("sections.influence.writeToUsBox.items.bugReports.title")}</strong> —{" "}
                {t("sections.influence.writeToUsBox.items.bugReports.body")}
              </Bullet>
              <Bullet>
                <strong>{t("sections.influence.writeToUsBox.items.newDepartment.title")}</strong> —{" "}
                {t("sections.influence.writeToUsBox.items.newDepartment.body")}
              </Bullet>
              <Bullet>
                <strong>{t("sections.influence.writeToUsBox.items.questions.title")}</strong> —{" "}
                {t("sections.influence.writeToUsBox.items.questions.body")}
              </Bullet>
            </Bullets>
          </div>
          <p>{t("sections.influence.contactNote")}</p>
          <p>
            {t("sections.influence.seeAlso.before")}{" "}
            <Link to="/about" className="text-accent underline underline-offset-2">
              {t("sections.influence.seeAlso.aboutLink")}
            </Link>
            {t("sections.influence.seeAlso.middle")}{" "}
            <Link to="/security" className="text-accent underline underline-offset-2">
              {t("sections.influence.seeAlso.securityLink")}
            </Link>
            .
          </p>
        </Section>
      </div>
    </LongPage>
  );
};
